import React, { createContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Project, UpdateObraData } from '../types';
import { API_BASE_URL } from '../constants';


interface ProjectsContextType {
  projects: Project[];
  loading: boolean;
  addProject: (newProjectData: Project) => Promise<{ success: boolean; message?: string }>;
  updateProject: (projectId: string, updateData: UpdateObraData) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  toggleLockProject: (projectId: string, userId: string) => Promise<void>;
}

export const ProjectsContext = createContext<ProjectsContextType | undefined>(undefined);

interface ProjectsProviderProps {
  children: ReactNode;
}

export const ProjectsProvider: React.FC<ProjectsProviderProps> = ({ children }) => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchProjects = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/projects`);
            if (!response.ok) throw new Error('Failed to fetch projects');
            const data: Project[] = await response.json();
            setProjects(data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    const addProject = useCallback(async (newProjectData: Project): Promise<{ success: boolean; message?: string; }> => {
        try {
            const response = await fetch(`${API_BASE_URL}/projects`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newProjectData),
            });
            
            const result = await response.json();

            if (!response.ok) {
                console.error('Falha ao adicionar projeto. Servidor respondeu:', result);
                return { success: false, message: result.message || 'Erro desconhecido do servidor.' };
            }
            
            const newProject = result;
            setProjects(current => [newProject, ...current]);
            return { success: true };

        } catch (error) {
            console.error('Erro de rede ou parsing ao adicionar projeto:', error);
            return { success: false, message: 'Erro de conexão. Verifique o console para mais detalhes.' };
        }
    }, []);

    const updateProject = useCallback(async (projectId: string, updateData: UpdateObraData) => {
        try {
            const response = await fetch(`${API_BASE_URL}/projects/${projectId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updateData),
            });
            if (!response.ok) throw new Error('Failed to update project');
            const updatedProject = await response.json();
            setProjects(current =>
                current.map(p => (p.id === updatedProject.id ? { ...p, ...updatedProject } : p))
            );
        } catch (error) {
            console.error(error);
        }
    }, []);

    const deleteProject = useCallback(async (projectId: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/projects/${projectId}`, {
                method: 'DELETE',
            });
            if (!response.ok) throw new Error('Failed to delete project');
            setProjects(current => current.filter(p => p.id !== projectId));
        } catch (error) {
            console.error(error);
        }
    }, []);

    const toggleLockProject = useCallback(async (projectId: string, userId: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/projects/toggle-lock/${projectId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId }),
            });
            
            const responseData = await response.json();
    
            if (!response.ok) {
                const message = responseData.message || 'Failed to toggle lock on project';
                throw new Error(message);
            }
            
            // SOLUÇÃO DEFINITIVA: Em vez de atualizar o estado localmente,
            // buscamos a lista inteira de projetos novamente do servidor.
            // Isso garante 100% de consistência com o banco de dados.
            await fetchProjects();
    
        } catch (error: any) {
            console.error(error);
            alert(error.message); // Show the specific error to the user (e.g., locked by another user)
        }
    }, [fetchProjects]);

    return (
        <ProjectsContext.Provider value={{ projects, loading, addProject, updateProject, deleteProject, toggleLockProject }}>
            {children}
        </ProjectsContext.Provider>
    );
};